import { useEffect, useMemo } from 'react';
import { type OpenApiSchemaIntegrationSyncType } from '#shared/api/schema';
import { useGetIntegrationStatuses } from '../api';
import {
    buildIntegrationsTypeStatusSearchParams,
    useStartIntegration,
    type UseStartIntegrationOptions,
} from './useStartIntegration';

/**
 * Подписка на статус интеграции, пулинг и рефетч истории (getStatus).
 * @kind helper
 */
export const useIntegrationHistorySync = (
    syncType: OpenApiSchemaIntegrationSyncType,
    options?: UseStartIntegrationOptions
) => {
    const statusSearchParams = useMemo(
        () => buildIntegrationsTypeStatusSearchParams(syncType, options?.integrationType, { per_page: 1 }),
        [syncType, options?.integrationType]
    );

    const { data, isFetching, isError } = useGetIntegrationStatuses(statusSearchParams);

    const status = data?.[0]?.status;
    const isWaitingToStart = (syncType === 'mc_feed' || syncType === 'mapping') && status === 'not_running';
    const isLastRequestProcessing = status === 'in_progress' || isWaitingToStart;

    const { handleIntegrationStart, canPolling, isProcessing, handlePolling, handleClean } = useStartIntegration(
        syncType,
        isFetching,
        options
    );

    useEffect(() => {
        if (!isLastRequestProcessing) {
            return;
        }

        handlePolling();
    }, [isLastRequestProcessing, handlePolling]);

    useEffect(() => {
        if (!canPolling) {
            return;
        }

        handlePolling();
    }, [canPolling, handlePolling]);

    useEffect(() => () => handleClean(), [handleClean]);

    return {
        isLastRequestProcessing,
        isProcessing,
        isError,
        handleIntegrationStart,
    };
};

export type UseIntegrationHistorySyncResult = ReturnType<typeof useIntegrationHistorySync>;
